import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { Check, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Folder, FolderOpen, Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { foldSearch } from '@/shared/lib/foldSearch';
import { Spinner } from '@/shared/ui/core/Loading';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/core/Popover';
import {
  controlHeightClass,
  errorClass,
  fieldBorderClass,
  fieldFocusClass,
  hintClass,
  labelClass,
} from '@/shared/ui/core/styles';
import { useFormFieldContext } from '@/shared/ui/core/formFieldContext';
import type { ControlSize, FieldTone } from '@/shared/ui/core/types';

export type TreeSelectNode = {
  value: string;
  label: string;
  code?: string;
  subLabel?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /**
   * If false, this node cannot be selected (e.g. category grouping only).
   * Defaults to: `selectableParents ? true : !hasChildren`.
   */
  selectable?: boolean;
  children?: TreeSelectNode[];
  [key: string]: unknown;
};

export type FlatTreeItem = {
  id: string;
  parentId?: string | null;
  label: string;
  value?: string;
  code?: string;
  subLabel?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  selectable?: boolean;
  [key: string]: unknown;
};

export function buildTreeFromFlat<T extends FlatTreeItem>(
  rows: ReadonlyArray<T>,
  options?: {
    idKey?: keyof T;
    parentIdKey?: keyof T;
    valueKey?: keyof T;
    labelKey?: keyof T;
  },
): TreeSelectNode[] {
  const idKey = options?.idKey ?? ('id' as keyof T);
  const parentIdKey = options?.parentIdKey ?? ('parentId' as keyof T);
  const valueKey = options?.valueKey ?? ('value' as keyof T);
  const labelKey = options?.labelKey ?? ('label' as keyof T);

  const ids = new Set(rows.map((r) => String(r[idKey])));
  const byParent = new Map<string | null, T[]>();

  for (const row of rows) {
    const pVal = row[parentIdKey] ? String(row[parentIdKey]) : null;
    const parentKey = pVal && ids.has(pVal) ? pVal : null;
    const list = byParent.get(parentKey) ?? [];
    list.push(row);
    byParent.set(parentKey, list);
  }

  const toNode = (row: T): TreeSelectNode => {
    const rowId = String(row[idKey]);
    const rowVal = row[valueKey] !== undefined ? String(row[valueKey]) : rowId;
    const rowLabel = String(row[labelKey] ?? rowVal);
    const children = (byParent.get(rowId) ?? []).map(toNode);

    return {
      value: rowVal,
      label: rowLabel,
      code: row.code ? String(row.code) : undefined,
      subLabel: row.subLabel ? String(row.subLabel) : undefined,
      icon: row.icon as React.ReactNode,
      disabled: Boolean(row.disabled),
      selectable: row.selectable !== undefined ? Boolean(row.selectable) : undefined,
      children: children.length > 0 ? children : undefined,
    };
  };

  return (byParent.get(null) ?? []).map(toNode);
}

export type TreeSelectProps = {
  nodes?: TreeSelectNode[];
  /** Alternately pass flat records with parentId */
  flatData?: ReadonlyArray<FlatTreeItem>;
  value?: string;
  onValueChange?: (value: string, selectedNode?: TreeSelectNode) => void;
  label?: string;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  size?: ControlSize;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  clearable?: boolean;
  /**
   * If true, clicking a parent node selects it; expanding/collapsing is done via the chevron button.
   * If false, clicking anywhere on a parent row toggles its expand/collapse.
   * Default is `false` (leaf nodes are preferred for selection).
   */
  selectableParents?: boolean;
  /**
   * Display mode in trigger input:
   * - 'full-path': shows full hierarchy path (e.g. "Nguyên liệu > Trái cây tươi")
   * - 'label-only': shows leaf label (e.g. "Trái cây tươi")
   * Default is 'full-path'.
   */
  displayMode?: 'full-path' | 'label-only';
  pathSeparator?: string;
  /** Default expand all on first render */
  defaultExpandAll?: boolean;
  /** Show folder icons automatically */
  showFolderIcons?: boolean;
  /** Show expand all / collapse all buttons in search bar. Default: false */
  showExpandCollapse?: boolean;
  loading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  id?: string;
  className?: string;
  rootClassName?: string;
  'aria-label'?: string;
};

type NodeMeta = {
  node: TreeSelectNode;
  path: string[];
  ancestorValues: Set<string>;
};

function collectMeta(nodes: TreeSelectNode[]): Map<string, NodeMeta> {
  const map = new Map<string, NodeMeta>();

  function walk(nodeList: TreeSelectNode[], currentPath: string[], ancestors: Set<string>) {
    for (const item of nodeList) {
      const nextPath = [...currentPath, item.label];
      map.set(item.value, {
        node: item,
        path: nextPath,
        ancestorValues: new Set(ancestors),
      });
      if (item.children && item.children.length > 0) {
        const nextAncestors = new Set(ancestors);
        nextAncestors.add(item.value);
        walk(item.children, nextPath, nextAncestors);
      }
    }
  }

  walk(nodes, [], new Set());
  return map;
}

function filterTree(nodes: TreeSelectNode[], query: string): TreeSelectNode[] {
  if (!query) return nodes;
  const folded = foldSearch(query);

  const walk = (node: TreeSelectNode): TreeSelectNode | null => {
    const children = (node.children ?? [])
      .map(walk)
      .filter((child): child is TreeSelectNode => child != null);

    const matchesLabel = foldSearch(node.label).includes(folded);
    const matchesCode = node.code ? foldSearch(node.code).includes(folded) : false;
    const matchesSub = node.subLabel ? foldSearch(node.subLabel).includes(folded) : false;
    const selfMatches = matchesLabel || matchesCode || matchesSub;

    if (!selfMatches && children.length === 0) return null;
    return {
      ...node,
      children: children.length > 0 ? children : undefined,
    };
  };

  return nodes.map(walk).filter((n): n is TreeSelectNode => n != null);
}

function collectAllBranchValues(nodes: TreeSelectNode[]): string[] {
  const values: string[] = [];
  function walk(list: TreeSelectNode[]) {
    for (const item of list) {
      if (item.children && item.children.length > 0) {
        values.push(item.value);
        walk(item.children);
      }
    }
  }
  walk(nodes);
  return values;
}

export const TreeSelect = forwardRef<HTMLButtonElement, TreeSelectProps>(
  function TreeSelect(
    {
      nodes: rawNodes,
      flatData,
      value = '',
      onValueChange,
      label,
      hint,
      error,
      tone,
      size = 'md',
      placeholder = 'Chọn…',
      searchPlaceholder = 'Tìm kiếm…',
      emptyText = 'Không tìm thấy kết quả phù hợp',
      disabled = false,
      clearable = true,
      selectableParents = false,
      displayMode = 'full-path',
      pathSeparator = ' > ',
      defaultExpandAll = false,
      showFolderIcons = false,
      showExpandCollapse = false,
      loading = false,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
      id,
      className,
      rootClassName,
      'aria-label': ariaLabel,
    },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const listId = `${fieldId}-tree`;
    const searchInputId = `${fieldId}-search`;
    const formField = useFormFieldContext();
    const effectiveError = error || (formField.inFormField ? formField.error : undefined);
    const describedBy = effectiveError ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
    const resolvedTone: FieldTone = tone ?? (effectiveError ? 'error' : 'default');
    const shouldRenderError = Boolean(effectiveError && (!formField.inFormField || (!formField.error && Boolean(error))));

    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;

    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

    // Source nodes memoized
    const treeData = useMemo(() => {
      if (rawNodes && rawNodes.length > 0) return rawNodes;
      if (flatData && flatData.length > 0) return buildTreeFromFlat(flatData);
      return [];
    }, [rawNodes, flatData]);

    const metaMap = useMemo(() => collectMeta(treeData), [treeData]);
    const allBranchValues = useMemo(() => collectAllBranchValues(treeData), [treeData]);

    // Active selected metadata
    const selectedMeta = useMemo(() => (value ? metaMap.get(value) : undefined), [value, metaMap]);

    const displayLabel = useMemo(() => {
      if (!selectedMeta) return '';
      if (displayMode === 'label-only') return selectedMeta.node.label;
      return selectedMeta.path.join(pathSeparator);
    }, [selectedMeta, displayMode, pathSeparator]);

    // Filtered tree
    const term = query.trim();
    const filteredTree = useMemo(() => filterTree(treeData, term), [treeData, term]);

    // Mutual exclusion: close when another select opens
    useEffect(() => {
      const handler = (e: Event) => {
        const customEvent = e as CustomEvent<{ id: string }>;
        if (customEvent.detail?.id !== fieldId) {
          if (isControlled) {
            controlledOnOpenChange?.(false);
          } else {
            setInternalOpen(false);
          }
          setQuery('');
        }
      };
      window.addEventListener('tm-select-open', handler);
      return () => window.removeEventListener('tm-select-open', handler);
    }, [fieldId, isControlled, controlledOnOpenChange]);

    // Click outside listener on capture phase
    useEffect(() => {
      if (!open) return;
      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null;
        if (!target) return;
        const triggerEl = document.getElementById(fieldId);
        if (triggerEl && triggerEl.contains(target)) return;
        const element = target instanceof Element ? target : target.parentElement;
        if (element?.closest('[data-radix-popper-content-wrapper]')) return;
        handleOpenChange(false);
      };
      document.addEventListener('pointerdown', handlePointerDown, true);
      return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, [open, fieldId]);

    const handleOpenChange = (next: boolean) => {
      if (next) {
        window.dispatchEvent(new CustomEvent('tm-select-open', { detail: { id: fieldId } }));
        if (selectedMeta) {
          setExpanded(new Set(selectedMeta.ancestorValues));
        } else if (defaultExpandAll) {
          setExpanded(new Set(allBranchValues));
        }
      } else {
        setQuery('');
      }

      if (isControlled) {
        controlledOnOpenChange?.(next);
      } else {
        setInternalOpen(next);
      }
    };

    const toggleExpand = useCallback((nodeVal: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(nodeVal)) {
          next.delete(nodeVal);
        } else {
          next.add(nodeVal);
        }
        return next;
      });
    }, []);

    const expandAll = useCallback(() => {
      setExpanded(new Set(allBranchValues));
    }, [allBranchValues]);

    const collapseAll = useCallback(() => {
      setExpanded(new Set());
    }, []);

    const pick = useCallback(
      (node: TreeSelectNode) => {
        onValueChange?.(node.value, node);
        handleOpenChange(false);
      },
      [onValueChange],
    );

    const clearSelection = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onValueChange?.('', undefined);
    };

    return (
      <div className={cn('flex w-full flex-col gap-1.5', rootClassName)}>
        {label ? (
          <label htmlFor={fieldId} className={labelClass}>
            {label}
          </label>
        ) : null}

        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              type="button"
              id={fieldId}
              disabled={disabled}
              aria-label={ariaLabel}
              aria-invalid={resolvedTone === 'error' || undefined}
              aria-describedby={describedBy}
              aria-haspopup="tree"
              aria-expanded={open}
              aria-controls={listId}
              className={cn(
                'group inline-flex w-full items-center justify-between gap-2 rounded-input border bg-surface px-3 text-left text-body text-text outline-none transition-colors select-none',
                fieldFocusClass,
                'disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-fg',
                !selectedMeta && 'text-text-muted',
                controlHeightClass[size],
                fieldBorderClass[resolvedTone],
                className,
              )}
            >
              <span className="min-w-0 flex-1 truncate">
                {displayLabel || placeholder}
              </span>

              <div className="flex shrink-0 items-center gap-1.5">
                {clearable && !disabled && Boolean(value) && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Xóa lựa chọn"
                    title="Xóa lựa chọn"
                    onClick={clearSelection}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        clearSelection(e as unknown as React.MouseEvent);
                      }
                    }}
                    className="flex size-4 items-center justify-center rounded-sm text-text-muted hover:bg-neutral-muted hover:text-text cursor-pointer transition-colors"
                  >
                    <X className="size-3" aria-hidden />
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    'size-4 text-text-secondary transition-transform duration-200',
                    open && 'rotate-180 text-primary',
                  )}
                  aria-hidden
                />
              </div>
            </button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0 shadow-lg border border-border bg-surface"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              document.getElementById(searchInputId)?.focus();
            }}
          >
            {/* Search Input Bar & Quick Actions */}
            <div className="border-b border-border p-2 bg-surface">
              <div className="flex items-center gap-1.5">
                <div className="relative flex-1">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-text-muted"
                    aria-hidden
                  />
                  <input
                    id={searchInputId}
                    type="text"
                    inputMode="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    autoComplete="off"
                    className={cn(
                      'h-7 w-full rounded-input border border-border bg-surface pr-7 pl-7 text-[12px] text-text',
                      'placeholder:text-text-muted outline-none transition-colors',
                      fieldFocusClass,
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // If only 1 node in filtered tree, pick it
                        const first = filteredTree[0];
                        if (first && filteredTree.length === 1 && (!first.children || first.children.length === 0)) {
                          if (!first.disabled) pick(first);
                        }
                      }
                    }}
                  />
                  {query && (
                    <button
                      type="button"
                      aria-label="Xóa tìm kiếm"
                      onClick={() => setQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text p-0.5"
                    >
                      <X className="size-3" aria-hidden />
                    </button>
                  )}
                </div>

                {showExpandCollapse && allBranchValues.length > 0 && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      title="Mở rộng tất cả"
                      aria-label="Mở rộng tất cả nhánh"
                      onClick={expandAll}
                      className="flex h-7 w-7 items-center justify-center rounded-input border border-border text-text-muted hover:bg-neutral-muted hover:text-text transition-colors cursor-pointer"
                    >
                      <ChevronsUpDown className="size-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      title="Thu gọn tất cả"
                      aria-label="Thu gọn tất cả nhánh"
                      onClick={collapseAll}
                      className="flex h-7 w-7 items-center justify-center rounded-input border border-border text-text-muted hover:bg-neutral-muted hover:text-text transition-colors cursor-pointer"
                    >
                      <ChevronsDownUp className="size-3.5" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Tree View Content */}
            <div
              id={listId}
              role="tree"
              aria-label={ariaLabel ?? label ?? placeholder}
              className="max-h-[320px] overflow-y-auto p-1.5 outline-none select-none scrollbar-thin"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-caption text-text-muted">
                  <Spinner size="sm" />
                  <span>Đang tải danh mục…</span>
                </div>
              ) : filteredTree.length === 0 ? (
                <div className="py-6 text-center text-caption text-text-muted">
                  <p className="m-0">{emptyText}</p>
                </div>
              ) : (
                <TreeNodeList
                  nodes={filteredTree}
                  depth={0}
                  searching={term.length > 0}
                  expanded={expanded}
                  selectedVal={value}
                  selectableParents={selectableParents}
                  showFolderIcons={showFolderIcons}
                  onToggle={toggleExpand}
                  onPick={pick}
                />
              )}
            </div>
          </PopoverContent>
        </Popover>

        {shouldRenderError ? (
          <p id={`${fieldId}-error`} className={errorClass} role="alert">
            {effectiveError}
          </p>
        ) : hint ? (
          <p id={`${fieldId}-hint`} className={hintClass}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

function TreeNodeList({
  nodes,
  depth = 0,
  searching,
  expanded,
  selectedVal,
  selectableParents,
  showFolderIcons,
  onToggle,
  onPick,
}: {
  nodes: TreeSelectNode[];
  depth?: number;
  searching: boolean;
  expanded: ReadonlySet<string>;
  selectedVal: string;
  selectableParents: boolean;
  showFolderIcons: boolean;
  onToggle: (val: string) => void;
  onPick: (node: TreeSelectNode) => void;
}) {
  return (
    <ul role="group" className={cn('m-0 list-none p-0', depth > 0 && 'ml-4')}>
      {nodes.map((node, index) => {
        const hasChildren = Boolean(node.children && node.children.length > 0);
        const isOpen = searching || expanded.has(node.value);
        const isSelected = node.value === selectedVal;
        const isLast = index === nodes.length - 1;

        // Is this specific node selectable?
        const isSelectable =
          node.selectable !== undefined
            ? node.selectable
            : selectableParents
              ? true
              : !hasChildren;

        return (
          <li key={node.value} role="treeitem" aria-expanded={hasChildren ? isOpen : undefined} aria-selected={isSelected} className="relative">
            {/* Tree hierarchy guides (branch lines) */}
            {depth > 0 && !isLast && (
              <span
                aria-hidden
                className="pointer-events-none absolute top-0 bottom-0 left-0 w-px bg-border/70"
              />
            )}

            <div className="relative">
              {depth > 0 && (
                <>
                  {isLast && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute top-0 left-0 h-1/2 w-px bg-border/70"
                    />
                  )}
                  {/* Horizontal elbow connector */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 left-0 h-px w-2 -translate-y-1/2 bg-border/70"
                  />
                </>
              )}

              <div
                className={cn(
                  'group flex items-center gap-1.5 rounded-control py-1.5 pr-2.5 text-left text-[12px] leading-4 outline-none transition-colors',
                  depth > 0 ? 'ml-2 w-[calc(100%-8px)] pl-0.5' : 'w-full pl-1',
                  node.disabled && 'pointer-events-none opacity-50',
                  isSelected
                    ? 'bg-primary-soft font-medium text-primary'
                    : 'text-text hover:bg-neutral-muted/70 hover:text-text',
                )}
              >
                {/* Expand / Collapse Chevron trigger */}
                {hasChildren ? (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={isOpen ? 'Thu gọn' : 'Mở rộng'}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(node.value);
                    }}
                    className="flex size-5 shrink-0 items-center justify-center rounded-sm text-text-muted hover:bg-neutral-muted hover:text-text transition-colors cursor-pointer"
                  >
                    <ChevronRight
                      className={cn(
                        'size-3.5 transition-transform duration-150',
                        isOpen && 'rotate-90 text-text-secondary',
                      )}
                      aria-hidden
                    />
                  </button>
                ) : (
                  <span className="size-5 shrink-0" aria-hidden />
                )}

                {/* Optional folder/custom icon */}
                {node.icon ? (
                  <span className="flex size-4 shrink-0 items-center justify-center text-text-secondary">
                    {node.icon}
                  </span>
                ) : showFolderIcons && hasChildren ? (
                  <span className="flex size-4 shrink-0 items-center justify-center text-primary/70">
                    {isOpen ? <FolderOpen className="size-3.5" /> : <Folder className="size-3.5" />}
                  </span>
                ) : null}

                {/* Main Node Click Target */}
                <button
                  type="button"
                  tabIndex={0}
                  disabled={node.disabled}
                  className={cn(
                    'flex min-w-0 flex-1 items-center justify-between gap-2 border-0 bg-transparent p-0 text-left text-[12px] outline-none',
                    isSelectable ? 'cursor-pointer' : hasChildren ? 'cursor-pointer' : 'cursor-default',
                  )}
                  onClick={() => {
                    if (isSelectable) {
                      onPick(node);
                    } else if (hasChildren) {
                      onToggle(node.value);
                    }
                  }}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{node.label}</span>
                    {node.subLabel && (
                      <span className="text-[10px] text-text-muted truncate">{node.subLabel}</span>
                    )}
                  </div>

                  {/* Right-aligned Code / Badge & Check indicator */}
                  <div className="flex shrink-0 items-center gap-1.5 pl-1">
                    {node.code && (
                      <span
                        className={cn(
                          'font-mono text-[11px] font-normal tracking-tight',
                          isSelected ? 'text-primary' : 'text-text-muted group-hover:text-text-secondary',
                        )}
                      >
                        {node.code}
                      </span>
                    )}
                    <div className="flex w-3.5 items-center justify-center">
                      {isSelected && (
                        <Check className="size-3.5 text-primary" aria-hidden />
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Recursive Children */}
            {hasChildren && isOpen && node.children && (
              <TreeNodeList
                nodes={node.children}
                depth={depth + 1}
                searching={searching}
                expanded={expanded}
                selectedVal={selectedVal}
                selectableParents={selectableParents}
                showFolderIcons={showFolderIcons}
                onToggle={onToggle}
                onPick={onPick}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
